import createClient from 'openapi-fetch'
import type { paths } from '../api/generated.js'
import type { User } from './session.js'
import { apiBaseUrl } from '../api/base-url.js'

type MeResponse = paths['/auth/me']['get']['responses'][200]['content']['application/json']

// Dedicated client for auth endpoints - bypasses the main api client's 401 interceptor.
// (Otherwise a 401 on /auth/login would try to refresh, which is nonsense.)
const authClient = createClient<paths>({
  baseUrl: apiBaseUrl(),
  credentials: 'include',
})

export async function login(email: string, password: string): Promise<string> {
  const { data, error } = await authClient.POST('/auth/login', {
    body: { email, password },
  })
  if (error || !data) {
    const detail = (error as { detail?: string } | undefined)?.detail ?? 'login_failed'
    throw new Error(detail)
  }
  return data.accessToken
}

export async function attemptRefresh(): Promise<string | null> {
  const { data } = await authClient.POST('/auth/refresh', {})
  return data?.accessToken ?? null
}

export async function logout(): Promise<void> {
  await authClient.POST('/auth/logout', {})
}

export async function fetchMe(accessToken: string): Promise<User> {
  // Absolute URL via apiBaseUrl(): a relative '/api/v1/auth/me' hits the
  // static frontend origin (404) on the production cross-origin deploy.
  const res = await fetch(`${apiBaseUrl()}/auth/me`, {
    headers: { authorization: `Bearer ${accessToken}` },
    credentials: 'include',
  })
  if (!res.ok) throw new Error('me_failed')
  const body = (await res.json()) as MeResponse
  return { id: body.id, email: body.email, role: body.role }
}
